// components/CreateLeadModal.jsx
import React, { useState } from "react";
import { X, Building2, User, DollarSign, Target } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateLeadModal = ({ onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [activeSection, setActiveSection] = useState("basic");
  const [formData, setFormData] = useState({
    salutation: "",
    firstName: "",
    lastName: "",
    fullName: "",
    email: "",
    phone: "",
    mobilePhone: "",
    workPhone: "",
    fax: "",
    companyName: "",
    companyWebsite: "",
    companyIndustry: "",
    companySize: "",
    numberOfEmployees: "",
    annualRevenue: "",
    jobTitle: "",
    department: "",
    seniority: "",
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    linkedinUrl: "",
    twitterUrl: "",
    facebookUrl: "",
    status: "new",
    rating: "Cold",
    leadSource: "",
    score: "",
    amount: "",
    currency: "GBP",
    probability: 50,
    expectedRevenue: "",
    expectedCloseDate: "",
    opportunityStage: "",
    opportunityPriority: "medium",
    opportunityType: "",
    pipeline: "sales",
    nextStep: "",
    primaryCampaignSource: "",
    numberOfHandsets: "",
    monthlySpendWithCurrentProvider: "",
    currentPhoneSystemAge: "",
    unitsSold: "",
    contractTermLeft: "",
    contractEndDate: "",
    callerNotes: "",
    booked: false,
    bookedDate: "",
    twoMinChallenge: false,
    twoMinChallengeDate: "",
    nonQualSat: false,
    satNonQualDate: "",
    qualSat: false,
    qualSatDate: "",
    sold: false,
    soldDate: "",
    installComplete: false,
    installationDate: "",
    notSat: false,
    orderNumber: "",
    currentGenerators: "",
    trackingNumber: "",
    mainCompetitors: "",
    deliveryInstallationStatus: "",
    description: "",
    doNotCall: false,
    emailOptOut: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const convertEmptyToNull = (value) =>
    typeof value === "string" && value.trim() === "" ? null : value;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.email && !formData.phone) {
      setError("Please provide at least an email or phone number");
      return;
    }
    setLoading(true);
    try {
      const createData = {
        salutation: convertEmptyToNull(formData.salutation),
        firstName: convertEmptyToNull(formData.firstName),
        lastName: convertEmptyToNull(formData.lastName),
        fullName: convertEmptyToNull(formData.fullName),
        email: convertEmptyToNull(formData.email),
        phone: convertEmptyToNull(formData.phone),
        mobilePhone: convertEmptyToNull(formData.mobilePhone),
        workPhone: convertEmptyToNull(formData.workPhone),
        fax: convertEmptyToNull(formData.fax),
        companyName: convertEmptyToNull(formData.companyName),
        companyWebsite: convertEmptyToNull(formData.companyWebsite),
        companyIndustry: convertEmptyToNull(formData.companyIndustry),
        companySize: convertEmptyToNull(formData.companySize),
        numberOfEmployees: formData.numberOfEmployees
          ? parseInt(formData.numberOfEmployees)
          : null,
        annualRevenue: formData.annualRevenue
          ? parseFloat(formData.annualRevenue)
          : null,
        jobTitle: convertEmptyToNull(formData.jobTitle),
        department: convertEmptyToNull(formData.department),
        seniority: convertEmptyToNull(formData.seniority),
        street: convertEmptyToNull(formData.street),
        city: convertEmptyToNull(formData.city),
        state: convertEmptyToNull(formData.state),
        postalCode: convertEmptyToNull(formData.postalCode),
        country: convertEmptyToNull(formData.country),
        linkedinUrl: convertEmptyToNull(formData.linkedinUrl),
        twitterUrl: convertEmptyToNull(formData.twitterUrl),
        facebookUrl: convertEmptyToNull(formData.facebookUrl),
        status: convertEmptyToNull(formData.status),
        rating: convertEmptyToNull(formData.rating),
        leadSource: convertEmptyToNull(formData.leadSource),
        score: formData.score ? parseInt(formData.score) : null,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        currency: convertEmptyToNull(formData.currency),
        probability: formData.probability ? parseInt(formData.probability) : 50,
        expectedRevenue: formData.expectedRevenue
          ? parseFloat(formData.expectedRevenue)
          : null,
        expectedCloseDate: convertEmptyToNull(formData.expectedCloseDate),
        opportunityStage: convertEmptyToNull(formData.opportunityStage),
        opportunityPriority: convertEmptyToNull(formData.opportunityPriority),
        opportunityType: convertEmptyToNull(formData.opportunityType),
        pipeline: convertEmptyToNull(formData.pipeline),
        nextStep: convertEmptyToNull(formData.nextStep),
        primaryCampaignSource: convertEmptyToNull(
          formData.primaryCampaignSource,
        ),
        numberOfHandsets: formData.numberOfHandsets
          ? parseInt(formData.numberOfHandsets)
          : null,
        monthlySpendWithCurrentProvider:
          formData.monthlySpendWithCurrentProvider
            ? parseFloat(formData.monthlySpendWithCurrentProvider)
            : null,
        currentPhoneSystemAge: formData.currentPhoneSystemAge
          ? parseFloat(formData.currentPhoneSystemAge)
          : null,
        unitsSold: formData.unitsSold ? parseFloat(formData.unitsSold) : null,
        contractTermLeft: formData.contractTermLeft
          ? parseFloat(formData.contractTermLeft)
          : null,
        contractEndDate: convertEmptyToNull(formData.contractEndDate),
        callerNotes: convertEmptyToNull(formData.callerNotes),
        booked: formData.booked,
        bookedDate: convertEmptyToNull(formData.bookedDate),
        twoMinChallenge: formData.twoMinChallenge,
        twoMinChallengeDate: convertEmptyToNull(formData.twoMinChallengeDate),
        nonQualSat: formData.nonQualSat,
        satNonQualDate: convertEmptyToNull(formData.satNonQualDate),
        qualSat: formData.qualSat,
        qualSatDate: convertEmptyToNull(formData.qualSatDate),
        sold: formData.sold,
        soldDate: convertEmptyToNull(formData.soldDate),
        installComplete: formData.installComplete,
        installationDate: convertEmptyToNull(formData.installationDate),
        notSat: formData.notSat,
        orderNumber: convertEmptyToNull(formData.orderNumber),
        currentGenerators: convertEmptyToNull(formData.currentGenerators),
        trackingNumber: convertEmptyToNull(formData.trackingNumber),
        mainCompetitors: convertEmptyToNull(formData.mainCompetitors),
        deliveryInstallationStatus: convertEmptyToNull(
          formData.deliveryInstallationStatus,
        ),
        description: convertEmptyToNull(formData.description),
        doNotCall: formData.doNotCall,
        emailOptOut: formData.emailOptOut,
      };
      await apiService.createLead(createData);
      onSuccess();
    } catch (error) {
      console.error("Error creating lead:", error);
      setError(error.response?.data?.message || "Failed to create lead");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const sections = [
    { id: "basic", label: "Basic Information", icon: User },
    { id: "company", label: "Company", icon: Building2 },
    { id: "financial", label: "Financial", icon: DollarSign },
    { id: "requirements", label: "Requirements", icon: Target },
    { id: "dealScore", label: "Deal Score", icon: Target },
    { id: "additional", label: "Additional", icon: Target },
  ];

  const getCurrencySymbol = (currency = "GBP") => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
    };
    return symbols[currency] || currency;
  };

  // Theme helpers
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
    isDarkMode
      ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;

  const sectionHeadingClass = `text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`;

  const subHeadingClass = `text-md font-semibold mb-3 mt-4 ${isDarkMode ? "text-white" : "text-gray-800"}`;

  const dealScorePanel = `rounded-lg p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`;

  const checkboxLabel = `flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-4xl max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 border-b p-6 flex items-center justify-between z-10 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <h2
            className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Create New Lead
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Section Tabs */}
        <div
          className={`border-b ${
            isDarkMode
              ? "border-gray-700 bg-[#13161B]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex overflow-x-auto">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                    activeSection === section.id
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} />
                    {section.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Basic Information Section */}
          {activeSection === "basic" && (
            <div className="space-y-4">
              <h3 className={sectionHeadingClass}>Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Salutation</label>
                  <select
                    name="salutation"
                    value={formData.salutation}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Dr.">Dr.</option>
                    <option value="Prof.">Prof.</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Doe"
                  />
                </div>
                <div>
                  <label className={labelClass}>Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="john.doe@example.com"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Phone <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+44 20 1234 5678"
                  />
                </div>
                <div>
                  <label className={labelClass}>Mobile Phone</label>
                  <input
                    type="tel"
                    name="mobilePhone"
                    value={formData.mobilePhone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+44 7700 900000"
                  />
                </div>
                <div>
                  <label className={labelClass}>Work Phone</label>
                  <input
                    type="tel"
                    name="workPhone"
                    value={formData.workPhone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+44 20 1234 5678"
                  />
                </div>
                <div>
                  <label className={labelClass}>Job Title</label>
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Sales Manager"
                  />
                </div>
                <div>
                  <label className={labelClass}>Department</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Sales"
                  />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="unqualified">Unqualified</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Rating</label>
                  <select
                    name="rating"
                    value={formData.rating}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Cold">Cold</option>
                    <option value="Warm">Warm</option>
                    <option value="Hot">Hot</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Lead Source</label>
                  <input
                    type="text"
                    name="leadSource"
                    value={formData.leadSource}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Web, Phone, Email"
                  />
                </div>
                <div>
                  <label className={labelClass}>Score (0-100)</label>
                  <input
                    type="number"
                    name="score"
                    value={formData.score}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className={inputClass}
                    placeholder="75"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className={inputClass}
                    placeholder="Additional notes about this lead..."
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className={checkboxLabel}>
                    <input
                      type="checkbox"
                      name="doNotCall"
                      checked={formData.doNotCall}
                      onChange={handleChange}
                      className="rounded"
                    />
                    Do Not Call
                  </label>
                  <label className={checkboxLabel}>
                    <input
                      type="checkbox"
                      name="emailOptOut"
                      checked={formData.emailOptOut}
                      onChange={handleChange}
                      className="rounded"
                    />
                    Email Opt Out
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Company Information Section */}
          {activeSection === "company" && (
            <div className="space-y-4">
              <h3 className={sectionHeadingClass}>Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Acme Corporation"
                  />
                </div>
                <div>
                  <label className={labelClass}>Company Website</label>
                  <input
                    type="url"
                    name="companyWebsite"
                    value={formData.companyWebsite}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className={labelClass}>Industry</label>
                  <input
                    type="text"
                    name="companyIndustry"
                    value={formData.companyIndustry}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Technology, Healthcare, etc."
                  />
                </div>
                <div>
                  <label className={labelClass}>Company Size</label>
                  <input
                    type="text"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Small, Medium, Enterprise"
                  />
                </div>
                <div>
                  <label className={labelClass}>Number of Employees</label>
                  <input
                    type="number"
                    name="numberOfEmployees"
                    value={formData.numberOfEmployees}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className={labelClass}>Annual Revenue (£)</label>
                  <input
                    type="number"
                    name="annualRevenue"
                    value={formData.annualRevenue}
                    onChange={handleChange}
                    step="0.01"
                    className={inputClass}
                    placeholder="1000000"
                  />
                </div>

                <div className="md:col-span-2">
                  <h4 className={subHeadingClass}>Location</h4>
                </div>
                <div>
                  <label className={labelClass}>Street</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="123 Main Street"
                  />
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="London"
                  />
                </div>
                <div>
                  <label className={labelClass}>State/Province</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="England"
                  />
                </div>
                <div>
                  <label className={labelClass}>Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="SW1A 1AA"
                  />
                </div>
                <div>
                  <label className={labelClass}>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="United Kingdom"
                  />
                </div>

                <div className="md:col-span-2">
                  <h4 className={subHeadingClass}>Social Media</h4>
                </div>
                <div>
                  <label className={labelClass}>LinkedIn URL</label>
                  <input
                    type="url"
                    name="linkedinUrl"
                    value={formData.linkedinUrl}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="https://linkedin.com/in/johndoe"
                  />
                </div>
                <div>
                  <label className={labelClass}>Twitter URL</label>
                  <input
                    type="url"
                    name="twitterUrl"
                    value={formData.twitterUrl}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="https://twitter.com/johndoe"
                  />
                </div>
                <div>
                  <label className={labelClass}>Facebook URL</label>
                  <input
                    type="url"
                    name="facebookUrl"
                    value={formData.facebookUrl}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="https://facebook.com/johndoe"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Financial Information Section */}
          {activeSection === "financial" && (
            <div className="space-y-4">
              <h3 className={sectionHeadingClass}>Financial Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Expected Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                      {getCurrencySymbol(formData.currency)}
                    </span>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      step="0.01"
                      className={`${inputClass} pl-10`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="JPY">JPY (¥)</option>
                    <option value="AUD">AUD (A$)</option>
                    <option value="CAD">CAD (C$)</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Win Probability (%)</label>
                  <input
                    type="number"
                    name="probability"
                    value={formData.probability}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className={inputClass}
                    placeholder="50"
                  />
                </div>
                <div>
                  <label className={labelClass}>Expected Revenue</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                      {getCurrencySymbol(formData.currency)}
                    </span>
                    <input
                      type="number"
                      name="expectedRevenue"
                      value={formData.expectedRevenue}
                      onChange={handleChange}
                      step="0.01"
                      className={`${inputClass} pl-10`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Expected Close Date</label>
                  <input
                    type="date"
                    name="expectedCloseDate"
                    value={formData.expectedCloseDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Opportunity Stage</label>
                  <select
                    name="opportunityStage"
                    value={formData.opportunityStage}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">--None--</option>
                    <option value="qualification">Qualification</option>
                    <option value="needs_analysis">Needs Analysis</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closed_won">Closed Won</option>
                    <option value="closed_lost">Closed Lost</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Priority</label>
                  <select
                    name="opportunityPriority"
                    value={formData.opportunityPriority}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Opportunity Type</label>
                  <input
                    type="text"
                    name="opportunityType"
                    value={formData.opportunityType}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="New Business, Renewal, Upsell"
                  />
                </div>
                <div>
                  <label className={labelClass}>Pipeline</label>
                  <select
                    name="pipeline"
                    value={formData.pipeline}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="sales">Sales</option>
                    <option value="renewal">Renewal</option>
                    <option value="upsell">Upsell</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Next Step</label>
                  <input
                    type="text"
                    name="nextStep"
                    value={formData.nextStep}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Follow-up call, Send proposal"
                  />
                </div>
                <div>
                  <label className={labelClass}>Primary Campaign Source</label>
                  <input
                    type="text"
                    name="primaryCampaignSource"
                    value={formData.primaryCampaignSource}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Google Ads, LinkedIn Campaign"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Requirements Section */}
          {activeSection === "requirements" && (
            <div className="space-y-4">
              <h3 className={sectionHeadingClass}>Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Number of Handsets / Phone Extensions
                  </label>
                  <input
                    type="number"
                    name="numberOfHandsets"
                    value={formData.numberOfHandsets}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Monthly Spend with Current Provider
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                      {getCurrencySymbol(formData.currency)}
                    </span>
                    <input
                      type="number"
                      name="monthlySpendWithCurrentProvider"
                      value={formData.monthlySpendWithCurrentProvider}
                      onChange={handleChange}
                      step="0.01"
                      className={`${inputClass} pl-10`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    Current Phone System Age (Years)
                  </label>
                  <input
                    type="number"
                    name="currentPhoneSystemAge"
                    value={formData.currentPhoneSystemAge}
                    onChange={handleChange}
                    step="0.1"
                    className={inputClass}
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className={labelClass}>Units Sold</label>
                  <input
                    type="number"
                    name="unitsSold"
                    value={formData.unitsSold}
                    onChange={handleChange}
                    step="0.01"
                    className={inputClass}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Contract Term Left (Years)
                  </label>
                  <input
                    type="number"
                    name="contractTermLeft"
                    value={formData.contractTermLeft}
                    onChange={handleChange}
                    step="0.1"
                    className={inputClass}
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className={labelClass}>Contract End Date</label>
                  <input
                    type="date"
                    name="contractEndDate"
                    value={formData.contractEndDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Caller Notes</label>
                  <textarea
                    name="callerNotes"
                    value={formData.callerNotes}
                    onChange={handleChange}
                    rows={4}
                    className={inputClass}
                    placeholder="Enter notes from call with prospect..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Deal Score Section */}
          {activeSection === "dealScore" && (
            <div className="space-y-4">
              <h3 className={sectionHeadingClass}>Deal Score</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: "booked", label: "BOOKED", dateName: "bookedDate" },
                  {
                    name: "twoMinChallenge",
                    label: "2 MIN CHALLENGE",
                    dateName: "twoMinChallengeDate",
                  },
                  {
                    name: "nonQualSat",
                    label: "NON QUAL SAT",
                    dateName: "satNonQualDate",
                  },
                  {
                    name: "qualSat",
                    label: "QUAL SAT",
                    dateName: "qualSatDate",
                  },
                  { name: "sold", label: "SOLD", dateName: "soldDate" },
                  {
                    name: "installComplete",
                    label: "Install Complete",
                    dateName: "installationDate",
                  },
                ].map(({ name, label, dateName }) => (
                  <div key={name} className={dealScorePanel}>
                    <label className="flex items-center gap-3 mb-2">
                      <input
                        type="checkbox"
                        name={name}
                        checked={formData[name]}
                        onChange={handleChange}
                        className="w-5 h-5 rounded"
                      />
                      <span
                        className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {label}
                      </span>
                    </label>
                    {formData[name] && (
                      <input
                        type="date"
                        name={dateName}
                        value={formData[dateName]}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    )}
                  </div>
                ))}
                <div className={dealScorePanel}>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="notSat"
                      checked={formData.notSat}
                      onChange={handleChange}
                      className="w-5 h-5 rounded"
                    />
                    <span
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      NOT SAT
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Additional Information Section */}
          {activeSection === "additional" && (
            <div className="space-y-4">
              <h3 className={sectionHeadingClass}>Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Order Number</label>
                  <input
                    type="text"
                    name="orderNumber"
                    value={formData.orderNumber}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="ORD-12345"
                  />
                </div>
                <div>
                  <label className={labelClass}>Current Generators</label>
                  <input
                    type="text"
                    name="currentGenerators"
                    value={formData.currentGenerators}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Generator info"
                  />
                </div>
                <div>
                  <label className={labelClass}>Tracking Number</label>
                  <input
                    type="text"
                    name="trackingNumber"
                    value={formData.trackingNumber}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="TRK-12345"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Delivery/Installation Status
                  </label>
                  <select
                    name="deliveryInstallationStatus"
                    value={formData.deliveryInstallationStatus}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="delayed">Delayed</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Main Competitors</label>
                  <textarea
                    name="mainCompetitors"
                    value={formData.mainCompetitors}
                    onChange={handleChange}
                    rows={3}
                    className={inputClass}
                    placeholder="List main competitors..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            className={`flex items-center gap-3 pt-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                  : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating..." : "Create Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLeadModal;
